/**
 * Epic SmartPhrase placeholder grammar — parser + renderer.
 *
 * Turns a phrase body (clean text, fences already stripped) into ordered
 * segments so we can (a) drive the fill-in note builder and (b) render the
 * three copy modes. The token shapes, with real counts from src/markdown:
 *
 *   ***                     free-text wildcard; auto-labeled (shown as a faint
 *                           caption) from the word before it ("Na ***" → "Na")
 *   *** (guidance)          wildcard + guidance: the parens become the form's
 *                           hint and stay in Epic copy, but vanish once filled
 *                           so nothing redundant is left to delete. A slash
 *                           list — "(witnessed/unwitnessed)" — becomes a
 *                           pick-one; units/ranges/prose stay free text  (154×)
 *   ***[hint]  *** [hint]   wildcard + a quiet, form-only label ("LVEF ***[%]");
 *                           the bracket is stripped from every output mode
 *   {Label: id}             SmartList — free text by default; options live in
 *                           Epic, not here                (~1800×)
 *   {Label: id::"default"}  single inline option → a prefilled default
 *   {Label: id::"a","b"}    inline option list → pick-one (or multi)
 *   {*Tip* …:26011}         author guidance — never in the signed note
 *   @NAME@ @AGE@ …          chart-pulls — Epic fills from the chart
 *   yes/no  has/not  …      slash-choices — author shorthand in prose
 *
 * Quoted options may themselves contain commas and ***, so options are parsed
 * by scanning quoted strings rather than splitting on commas.
 */

export type FieldKind = 'text' | 'choice' | 'multi' | 'chartpull';

export type PlaceholderField = {
  /** Stable per-occurrence key, used as the builder's form-state key. */
  key: string;
  kind: FieldKind;
  /** Human-readable label shown in the builder. */
  label: string;
  /** choice / multi only: the selectable options. */
  options?: string[];
  /** Prefilled value (single-option `::"default"`), if any. */
  default?: string;
  /** Exact original source text, reproduced verbatim in Epic mode. */
  raw: string;
  /**
   * True when `label` was inferred from the surrounding prose (a bare `***`),
   * rather than authored. The builder shows these as a faint caption so they
   * don't read as a value to retype; explicit guidance stays prominent.
   */
  inferred?: boolean;
};

export type Segment =
  | { type: 'literal'; text: string }
  | { type: 'tip'; raw: string }
  | { type: 'field'; field: PlaceholderField };

export type RenderMode = 'epic' | 'plain' | 'filled';

/** User-entered values, keyed by PlaceholderField.key. */
export type FieldValues = Record<string, string | string[]>;

// ---- Slash-choices ------------------------------------------------------
// A tight whitelist (longest first so multi-word phrases win) applied only to
// literal prose, so we never touch dates (7/6/22), and/or, etc.
const SLASH_CHOICES = [
  'denies/admits to',
  'positive/negative',
  'present/absent',
  'normal/abnormal',
  'interm/persistent',
  'admits to/denies',
  'has/not',
  'yes/no',
];
const SLASH_RE = new RegExp(
  '(' + SLASH_CHOICES.map((s) => s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')).join('|') + ')',
  'gi'
);

// ---- Chart-pulls --------------------------------------------------------
const CHART_LABELS: Record<string, string> = {
  NAME: 'Patient name', LNAME: 'Last name', FNAME: 'First name',
  AGE: 'Age', SEX: 'Sex', DOB: 'Date of birth',
  TODAYDATE: "Today's date", TD: "Today's date", NOW: 'Current time',
  ADMITDT: 'Admit date', VS: 'Vitals', VSRANGES: 'Vital ranges',
  PMH: 'Past medical history', PSH: 'Past surgical history',
  ALLERGY: 'Allergies', MEDS: 'Medications', PCP: 'PCP',
  ME: 'Me (author)', MECRED: 'My credentials', HIS: 'His/Her', HE: 'He/She',
  CC: 'Chief complaint', FAMHX: 'Family history', FOLLOWUP: 'Follow-up',
  ECG: 'ECG', RISRSLT: 'Imaging results', CAPHE: 'He/She (capitalized)',
};

function chartLabel(code: string): string {
  return CHART_LABELS[code] ?? humanizeToken(code);
}

function humanizeToken(code: string): string {
  const lower = code.toLowerCase();
  return lower.charAt(0).toUpperCase() + lower.slice(1);
}

// ---- Label cleanup ------------------------------------------------------
function cleanLabel(head: string, kind: FieldKind): string {
  const label = head
    .split(/[:;]/)[0] // drop the `: id` / `; range` tail
    .replace(/\s+/g, ' ')
    .trim();
  if (!label || /^blank(\s|$)/i.test(label)) {
    if (kind === 'multi') return 'Select all that apply';
    if (kind === 'choice') return 'Choose one';
    return 'Fill in';
  }
  return label;
}

/**
 * A bare `***` has no label of its own, but the prose almost always names it
 * right before the stars — "LVEF ***", "Na ***", "Last cath: ***". Lift that
 * word/key out so the builder can size and caption the slot instead of showing
 * a generic "Fill in". `before` is the literal text since the previous token;
 * a `***` at the start of a line yields no label (falls back to "Fill in").
 */
function wildcardLabel(before: string): string {
  const FALLBACK = 'Fill in';
  // Same line only — a *** at line start has no inline label.
  const line = before.slice(before.lastIndexOf('\n') + 1).replace(/\s+$/, '');
  if (!line) return FALLBACK;
  // "Key: ***" → the key, ignoring any trailing fragment of a previous
  // sentence on the same line ("...test: ***. Last cath: ***" → "Last cath").
  const key = line.match(/([A-Za-z0-9%][\w%/+().\- ]*?)\s*:\s*$/);
  if (key && key[1].length <= 40) return key[1];
  // Otherwise the token sitting right before the stars ("Na", "BNP/NT-proBNP").
  const tok = line.match(/[A-Za-z0-9%][\w%/+().-]*$/);
  return tok ? tok[0] : FALLBACK;
}

/**
 * Guidance parens that read as a slash list — `(witnessed/unwitnessed)`,
 * `(STEMI / no STEMI)`, `(hypo/distributive/cardio/obstructive)` — become a
 * pick-one. We bail on anything that looks like a unit, range, threshold,
 * comma/semicolon list, URL, "e.g." example, or a medical abbreviation
 * (mg/dL, 130/80, e.g. …, http://…, r/o typhlitis) so those stay free-text
 * guidance. Returns the options, or null when it isn't a clean choice.
 */
function parenChoice(content: string): string[] | null {
  if (!content.includes('/')) return null;
  if (/[,;:<>=%\d]|µ|e\.g\.|:\/\/|\b[a-z]\/[a-z]\b/i.test(content)) return null;
  const parts = content.split('/').map((s) => s.trim());
  if (parts.length < 2 || parts.some((p) => !p || p.length > 24)) return null;
  return parts;
}

/** Pull double-quoted strings out of an inline option list. */
function parseQuotedList(s: string): string[] {
  const out: string[] = [];
  const re = /"((?:[^"\\]|\\.)*)"/g;
  let m: RegExpExecArray | null;
  while ((m = re.exec(s))) out.push(m[1].trim());
  return out;
}

// Matches the next brace token, chart-pull, or wildcard run. A wildcard may
// carry guidance right after the stars: a bracket label `***[hint]` (group 4,
// consumed so it never reaches output) or parens `*** (guidance)` (group 5,
// kept in Epic). The parens lookahead refuses any group that contains its own
// `***`, so `*** (and Δ 1-3h ***)` is left as prose with two separate blanks.
const TOKEN_RE =
  /(\{[^{}]*\})|(@[A-Za-z][A-Za-z0-9]*@)|(\*{3,})(?:[ \t]*(?:\[([^\]\n]*)\]|\((?![^)\n]*\*\*\*)([^()\n]*)\)))?/g;

/**
 * Parse a template body into ordered segments. `keyPrefix` namespaces field
 * keys so multiple sections of one phrase don't collide.
 */
export function parseTemplate(text: string, keyPrefix = 'f'): Segment[] {
  const segments: Segment[] = [];
  let fieldCount = 0;
  const nextKey = () => `${keyPrefix}-${fieldCount++}`;

  // Split literal prose, lifting slash-choices into choice fields.
  const pushLiteral = (chunk: string) => {
    if (!chunk) return;
    let last = 0;
    for (const m of chunk.matchAll(SLASH_RE)) {
      const idx = m.index ?? 0;
      if (idx > last) segments.push({ type: 'literal', text: chunk.slice(last, idx) });
      const raw = m[0];
      segments.push({
        type: 'field',
        field: {
          key: nextKey(),
          kind: 'choice',
          label: 'Choose one',
          options: raw.split('/').map((o) => o.trim()),
          raw,
        },
      });
      last = idx + raw.length;
    }
    if (last < chunk.length) segments.push({ type: 'literal', text: chunk.slice(last) });
  };

  const pushBrace = (raw: string) => {
    const inner = raw.slice(1, -1).trim();
    if (inner.startsWith('*')) {
      segments.push({ type: 'tip', raw });
      return;
    }
    const sep = inner.indexOf('::');
    const head = sep >= 0 ? inner.slice(0, sep) : inner;
    const options = sep >= 0 ? parseQuotedList(inner.slice(sep + 2)) : [];

    let field: PlaceholderField;
    if (options.length === 0) {
      field = { key: nextKey(), kind: 'text', label: cleanLabel(head, 'text'), raw };
    } else if (options.length === 1) {
      // `::"default"` — a prefilled free-text default.
      field = {
        key: nextKey(), kind: 'text', label: cleanLabel(head, 'text'),
        default: options[0], raw,
      };
    } else {
      const kind: FieldKind = /\bmultiple\b/i.test(head) ? 'multi' : 'choice';
      field = { key: nextKey(), kind, label: cleanLabel(head, kind), options, raw };
    }
    segments.push({ type: 'field', field });
  };

  let last = 0;
  for (const m of text.matchAll(TOKEN_RE)) {
    const idx = m.index ?? 0;
    if (idx > last) pushLiteral(text.slice(last, idx));
    if (m[1]) pushBrace(m[1]);
    else if (m[2]) {
      const code = m[2].slice(1, -1);
      segments.push({
        type: 'field',
        field: { key: nextKey(), kind: 'chartpull', label: chartLabel(code), raw: m[2] },
      });
    } else if (m[3]) {
      const stars = m[3];
      const bracket = m[4]?.trim();
      const paren = m[5]?.trim();
      const before = text.slice(last, idx);
      if (bracket !== undefined) {
        // `***[hint]` — a quiet, form-only label. raw is just the stars, so the
        // bracket never reaches plain/filled/epic output.
        segments.push({
          type: 'field',
          field: { key: nextKey(), kind: 'text', label: bracket || wildcardLabel(before), raw: stars },
        });
      } else if (paren !== undefined) {
        // `*** (guidance)` — kept in Epic (raw = stars + parens), but the parens
        // never appear in plain/filled, so a pre-filled value leaves no leftover.
        const options = parenChoice(paren);
        segments.push({
          type: 'field',
          field: options
            ? { key: nextKey(), kind: 'choice', label: 'Choose one', options, raw: m[0] }
            : { key: nextKey(), kind: 'text', label: paren, raw: m[0] },
        });
      } else {
        // Bare `***` — auto-label from the prose, flagged so the builder shows
        // it as a faint caption rather than a value to retype.
        segments.push({
          type: 'field',
          field: { key: nextKey(), kind: 'text', label: wildcardLabel(before), raw: stars, inferred: true },
        });
      }
    }
    last = idx + m[0].length;
  }
  if (last < text.length) pushLiteral(text.slice(last));

  return segments;
}

/** The fields a human actually fills in (excludes chart-pulls and tips). */
export function inputFields(segments: Segment[]): PlaceholderField[] {
  return segments
    .filter((s): s is Extract<Segment, { type: 'field' }> => s.type === 'field')
    .map((s) => s.field)
    .filter((f) => f.kind !== 'chartpull');
}

/** Count of fillable blanks — drives the "Fill in (N)" affordance. */
export function countBlanks(segments: Segment[]): number {
  return inputFields(segments).length;
}

/** Human-readable fallback for an unfilled field (plain / filled modes). */
function blankFor(field: PlaceholderField): string {
  switch (field.kind) {
    case 'chartpull':
      return `[${field.label}]`;
    case 'choice':
      return field.options && field.options.length <= 4
        ? `[${field.options.join(' / ')}]`
        : `[${field.label}]`;
    case 'multi':
      return `[${field.label}]`;
    default:
      return field.default ? field.default : '___';
  }
}

function renderField(field: PlaceholderField, values: FieldValues, mode: RenderMode): string {
  if (mode === 'epic') return field.raw;
  if (mode === 'plain') return blankFor(field);

  // filled
  const v = values[field.key];
  if (Array.isArray(v)) return v.length ? v.join(', ') : blankFor(field);
  if (typeof v === 'string' && v.trim()) return v.trim();
  return blankFor(field);
}

/** Assemble one section's output for the given mode. */
export function renderSegments(
  segments: Segment[],
  values: FieldValues,
  mode: RenderMode
): string {
  return segments
    .map((seg) => {
      if (seg.type === 'literal') return seg.text;
      if (seg.type === 'tip') return mode === 'epic' ? seg.raw : '';
      return renderField(seg.field, values, mode);
    })
    .join('');
}

/** Convenience: parse + render a raw body in one call (copy modes). */
export function renderBody(body: string, values: FieldValues, mode: RenderMode): string {
  return renderSegments(parseTemplate(body), values, mode);
}
